// @flow

import React, { PureComponent } from 'react';
import { graphql, QueryRenderer } from 'react-relay';
import styled from 'styled-components';
import { i18n } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Button from 'shared/components/common/Button';
import * as Actions from 'main-app/store/Actions';
import UpdateCompanyMutation from 'main-app/mutations/UpdateCompany';

const Container = styled.div`
  padding: 24px;
  max-width: 600px;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
`;

const Description = styled.p`
  font-size: 13px;
  color: #666;
  margin-bottom: 24px;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  border: 1px solid ${p => (p.selected ? '#1890ff' : '#e8e8e8')};
  border-radius: 6px;
  cursor: pointer;
  background: ${p => (p.selected ? '#f0f7ff' : '#fff')};

  &:hover {
    border-color: #1890ff;
  }
`;

const RadioText = styled.div`
  flex: 1;
`;

const RadioTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 2px;
`;

const RadioDesc = styled.div`
  font-size: 12px;
  color: #888;
`;

const CURRENCIES = [
  {
    value: 'USD',
    title: 'USD — US Dollar ($)',
    description: 'United States Dollar. Symbol: $',
  },
  {
    value: 'GBP',
    title: 'GBP — British Pound (£)',
    description: 'British Pound Sterling. Symbol: £',
  },
  {
    value: 'EUR',
    title: 'EUR — Euro (€)',
    description: 'Euro. Symbol: €',
  },
];

type State = {
  selectedCurrency: ?string,
  saving: boolean,
};

class CurrencySettings extends PureComponent<{}, State> {
  state = {
    selectedCurrency: null,
    saving: false,
  };

  handleSave = async () => {
    const { selectedCurrency } = this.state;
    if (!selectedCurrency) return;

    this.setState({ saving: true });

    try {
      await UpdateCompanyMutation.commit({
        variables: {
          input: { currency: selectedCurrency },
        },
      });
      Actions.alertNotification(
        i18n.t('Currency updated successfully.'),
        'success',
      );
    } catch (e) {
      Actions.alertNotification(e.message, 'error');
    }

    this.setState({ saving: false });
  };

  render() {
    const { selectedCurrency, saving } = this.state;

    return (
      <QueryRenderer
        environment={relayEnvironment}
        cacheConfig={{ force: true }}
        query={graphql`
          query CurrencySettingsQuery {
            viewer {
              id
              company {
                id
                currency
              }
            }
          }
        `}
        variables={{}}
        render={({ props }) => {
          if (!props) return <Loader />;

          const currentCurrency =
            selectedCurrency || props.viewer.company.currency || 'USD';

          return (
            <Container>
              <Title>{i18n.t('Currency')}</Title>
              <Description>
                {i18n.t(
                  'Choose the default currency used across your organization for orders, invoices, purchase orders, and other financial records.',
                )}
              </Description>
              <RadioGroup>
                {CURRENCIES.map(currency => (
                  <RadioOption
                    key={currency.value}
                    selected={currentCurrency === currency.value}
                    onClick={() =>
                      this.setState({ selectedCurrency: currency.value })
                    }
                  >
                    <input
                      type="radio"
                      checked={currentCurrency === currency.value}
                      onChange={() =>
                        this.setState({ selectedCurrency: currency.value })
                      }
                      style={{ marginTop: 2 }}
                    />
                    <RadioText>
                      <RadioTitle>{currency.title}</RadioTitle>
                      <RadioDesc>{currency.description}</RadioDesc>
                    </RadioText>
                  </RadioOption>
                ))}
              </RadioGroup>
              <Button
                theme="blue"
                loading={saving}
                onClick={this.handleSave}
                disabled={!selectedCurrency}
              >
                {i18n.t('Save Currency')}
              </Button>
            </Container>
          );
        }}
      />
    );
  }
}

export default CurrencySettings;
